/**
 * pages-simple-expense.ts — "บันทึกรายรับ-รายจ่าย" (Simple Mode)
 * เลือกโค้ดค่าใช้จ่าย → ระบบเติมหมวด P&L + ภาษีให้ → กดยืนยัน
 * (จุดที่จุดเด่นคู่แข่งมาลง: "ระบบเตรียมให้ คนอนุมัติ" + "ให้เลือกไม่ให้จำ")
 * บันทึกผ่าน POST /api/entries (สัญญาเดิม) — ไม่มี engine ใหม่
 */
import {
  EXPENSE_CODE_CATALOG,
  PNL_CATEGORY,
  resolveCodeDefaults,
  expenseCodeRedirect,
  donationKindOfCode,
  computeDonationDeduction,
  DONATION_RULES,
  type ExpenseCode,
  type ExpenseFamily,
} from '@rrp/shared';
import type { Page } from './router.js';
import { formatTHB } from './format.js';
import { getTenant } from './state.js';
import { createEntry, listCustomCodesApi, saveCustomCodeApi } from './api.js';
import { esc } from './ui.js';

export type Direction = 'expense' | 'income';

// ─────────────────────────────────────────────────────────────
// โค้ดที่ผู้ใช้เพิ่มเอง — backend ต่อกิจการ (โหมดข้อมูลจริง)
// fallback = localStorage ต่อเบราว์เซอร์ (โหมด demo / backend ล่ม)
// ─────────────────────────────────────────────────────────────
const CUSTOM_KEY = 'rrp_custom_expense_codes';

/** cache ที่ allCodes()/render อ่านแบบ sync — เติมตอน mount ผ่าน loadCustomCodes() */
let customCache: ExpenseCode[] = [];
let codeSource: 'backend' | 'local' = 'local';

function getLocalCustomCodes(): ExpenseCode[] {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as ExpenseCode[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function addLocalCustomCode(c: ExpenseCode): void {
  try {
    const all = getLocalCustomCodes().filter((x) => x.code !== c.code);
    all.push(c);
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

/** โหลด custom codes: backend ก่อน (ถ้า live), ไม่งั้น/ล้มเหลว → localStorage */
export async function loadCustomCodes(): Promise<void> {
  if (getTenant()) {
    try {
      customCache = await listCustomCodesApi();
      codeSource = 'backend';
      return;
    } catch {
      /* backend ล่ม/ตารางยังไม่มี → fallback local */
    }
  }
  customCache = getLocalCustomCodes();
  codeSource = 'local';
}

/** บันทึกโค้ดใหม่: backend (live) หรือ localStorage (demo/fallback) + อัปเดต cache */
async function persistCustomCode(c: ExpenseCode): Promise<void> {
  customCache = [...customCache.filter((x) => x.code !== c.code), c];
  if (codeSource === 'backend') {
    await saveCustomCodeApi({
      code: c.code,
      name: c.name,
      family: c.family,
      classification: c.classification,
      ...(c.pnlCategory ? { pnlCategory: c.pnlCategory } : {}),
      ...(c.whtRate !== undefined ? { whtRate: c.whtRate } : {}),
      ...(c.vat ? { vat: c.vat } : {}),
      ...(c.note ? { note: c.note } : {}),
    });
  } else {
    addLocalCustomCode(c);
  }
}

/** ตัวเลือกหมวด P&L สำหรับฟอร์มเพิ่มโค้ดเอง (value อังกฤษ, label ไทย) */
export const CATEGORY_CHOICES: [string, string][] = [
  [PNL_CATEGORY.MARKETING, 'ค่าการตลาด/โฆษณา'],
  [PNL_CATEGORY.COMMISSION, 'ค่าคอม/แพลตฟอร์ม'],
  [PNL_CATEGORY.TRANSPORT, 'ค่าส่ง/แพ็ก'],
  [PNL_CATEGORY.PAYROLL, 'เงินเดือน/พนักงาน'],
  [PNL_CATEGORY.PROFESSIONAL, 'ค่าวิชาชีพ'],
  [PNL_CATEGORY.FIXED, 'ค่าประจำ (เช่า/น้ำ/ไฟ)'],
  [PNL_CATEGORY.VARIABLE, 'ค่าผันแปร'],
  [PNL_CATEGORY.OTHER_OP, 'อื่นๆ'],
  [PNL_CATEGORY.INTEREST, 'ดอกเบี้ย/การเงิน'],
];

// ─────────────────────────────────────────────────────────────
// pure: สร้าง body สำหรับ POST /api/entries
// ─────────────────────────────────────────────────────────────
export interface SimpleEntryForm {
  direction: Direction;
  /** โค้ดที่เลือก (เฉพาะรายจ่าย) — จากแคตตาล็อกหรือ custom */
  code?: ExpenseCode;
  amount: string;
  date?: string;
  note?: string;
  counterparty?: string;
  docNo?: string;
  /** VAT ที่รวมอยู่ในจำนวนเงิน (ถ้าผู้ใช้กรอกเอง) */
  vat?: string;
  wht?: string;
  paymentMethod?: string;
  unpaid?: boolean;
}

export function parseMoney(raw: string | undefined): string | null {
  if (raw === undefined) return null;
  const cleaned = raw.replace(/[,\s฿]/g, '').trim();
  if (cleaned === '') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(2);
}

/**
 * สร้าง body: ยอด/วันที่จากผู้ใช้ + ค่าภาษี/หมวดจากโค้ด (auto-fill)
 * @throws ถ้าจำนวนเงินไม่ถูกต้อง หรือรายจ่ายไม่ได้เลือกโค้ด
 */
export function buildSimpleEntryBody(f: SimpleEntryForm): Record<string, unknown> {
  const total = parseMoney(f.amount);
  if (total === null || Number(total) <= 0) {
    throw new Error('กรุณากรอกจำนวนเงินให้ถูกต้อง (มากกว่า 0)');
  }

  const body: Record<string, unknown> = { totalAmount: total };
  if (f.date) body['eventDate'] = f.date;

  if (f.direction === 'income') {
    body['classification'] = 'income';
  } else {
    if (!f.code) throw new Error('กรุณาเลือกโค้ดค่าใช้จ่าย');
    const d = resolveCodeDefaults(f.code);
    body['classification'] = d.classification;
    if (d.pnlCategory) body['pnlCategory'] = d.pnlCategory;
    if (d.whtRate !== undefined) body['whtRate'] = d.whtRate;
    if (d.vatBasis) body['vatBasis'] = d.vatBasis;
    if (d.vatClaimability) body['vatClaimability'] = d.vatClaimability;
    if (d.taxDeductible === false) body['taxDeductible'] = false;
    // บันทึกโค้ดไว้ในหมายเหตุอ้างอิง (คู่ค้า/เอกสารยังกรอกแยกได้)
    body['costCenter'] = f.code.code;
  }

  const counterparty = f.counterparty?.trim();
  if (counterparty) body['counterpartyName'] = counterparty;
  const docNo = f.docNo?.trim();
  if (docNo) body['receiptNo'] = docNo;

  const vat = parseMoney(f.vat);
  if (vat && Number(vat) > 0) {
    body['vatAmount'] = vat;
    body['amountBeforeVat'] = (Number(total) - Number(vat)).toFixed(2);
  }
  const wht = parseMoney(f.wht);
  if (wht && Number(wht) > 0) body['whtAmount'] = wht;

  if (f.unpaid) {
    body['paidAmount'] = '0.00';
    body['paymentMethod'] = 'unpaid';
  } else {
    body['paidAmount'] = total;
    if (f.paymentMethod) body['paymentMethod'] = f.paymentMethod;
  }

  const note = f.note?.trim();
  if (note) body['reason'] = note;
  return body;
}

// ─────────────────────────────────────────────────────────────
// UI helpers
// ─────────────────────────────────────────────────────────────
function todayKey(): string {
  try {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

/** ลำดับกลุ่มที่โชว์ในตัวเลือก */
const FAMILY_ORDER: ExpenseFamily[] = [
  'ส่งและแพ็ก',
  'การตลาด',
  'คอมมิชชั่น',
  'เงินเดือนและพนักงาน',
  'ค่าวิชาชีพ',
  'ค่าประจำ',
  'ค่าผันแปร',
  'ดำเนินงานอื่น',
  'ดอกเบี้ยและการเงิน',
  'บริจาค',
  'พิเศษ (ตรวจสอบก่อน)',
];

/** ชื่อหมวดที่แม่ค้าเข้าใจง่าย + ไอคอน + คำอธิบายสั้น (คีย์ = family เดิม) */
interface FamMeta {
  icon: string;
  label: string;
  hint: string;
}
const FAMILY_DISPLAY: Record<string, FamMeta> = {
  'ส่งและแพ็ก': { icon: '📦', label: 'ค่าส่ง & แพ็กสินค้า', hint: 'ขนส่ง · กล่อง/เทป · ไปรษณีย์' },
  'การตลาด': { icon: '📣', label: 'โฆษณา & การตลาด', hint: 'ค่าแอด · ของแถม · พร้อพไลฟ์' },
  'คอมมิชชั่น': { icon: '🤝', label: 'ค่าคอมมิชชั่น', hint: 'ค่าคอมคนขาย/พันธมิตร' },
  'เงินเดือนและพนักงาน': { icon: '👥', label: 'เงินเดือน & พนักงาน', hint: 'เงินเดือน · โบนัส · ประกันสังคม' },
  'ค่าวิชาชีพ': { icon: '📒', label: 'ค่าบัญชี & กฎหมาย', hint: 'ทำบัญชี/สอบบัญชี · ที่ปรึกษา/ทนาย' },
  'ค่าประจำ': { icon: '🏢', label: 'ค่าใช้จ่ายประจำเดือน', hint: 'ค่าเช่า · น้ำ/ไฟ · เน็ต · ซอฟต์แวร์' },
  'ค่าผันแปร': { icon: '🔧', label: 'ของใช้ · ซ่อม · เดินทาง', hint: 'ของสิ้นเปลือง · ซ่อมแซม · ค่ารถ/น้ำมัน' },
  'ดำเนินงานอื่น': { icon: '💼', label: 'ค่าใช้จ่ายอื่น ๆ', hint: 'อาหาร · ที่พัก · อบรม · เบ็ดเตล็ด' },
  'ดอกเบี้ยและการเงิน': { icon: '🏦', label: 'ดอกเบี้ย & การเงิน', hint: 'ดอกเบี้ยเงินกู้' },
  'บริจาค': { icon: '🙏', label: 'เงินบริจาค', hint: 'วัด · โรงพยาบาลรัฐ · สถานศึกษา' },
  'พิเศษ (ตรวจสอบก่อน)': { icon: '⚠️', label: 'ต้องตรวจก่อน — ไม่ใช่ค่าใช้จ่าย', hint: 'ทรัพย์สิน · หนี้สิน · เงินยืม · ค่าปรับ' },
};
function famMeta(fam: string): FamMeta {
  return FAMILY_DISPLAY[fam] ?? { icon: '•', label: fam, hint: '' };
}

function allCodes(): ExpenseCode[] {
  return [...EXPENSE_CODE_CATALOG, ...customCache];
}

// โค้ดที่ใช้บ่อย (ล่าสุด) — เก็บ localStorage เพื่อกดซ้ำเร็ว ๆ (#3 ลดขั้นตอน)
const RECENT_KEY = 'rrp_recent_codes';
function getRecentCodes(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const arr = raw ? (JSON.parse(raw) as string[]) : [];
    return Array.isArray(arr) ? arr.slice(0, 6) : [];
  } catch {
    return [];
  }
}
function recordRecentCode(code: string): void {
  try {
    const next = [code, ...getRecentCodes().filter((c) => c !== code)].slice(0, 6);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}
/** แถบชิป "ใช้บ่อย" — โค้ดที่เพิ่งใช้ กดเลือกได้ทันที (ข้ามการค้นหา) */
function recentChipsHtml(): string {
  const codes = getRecentCodes()
    .map((code) => allCodes().find((c) => c.code === code))
    .filter((c): c is ExpenseCode => Boolean(c));
  if (codes.length === 0) return '';
  const chips = codes
    .map(
      (c) =>
        `<button type="button" class="recent-chip" data-code="${esc(c.code)}" title="${esc(c.name)}">${esc(c.name)}</button>`,
    )
    .join('');
  return `<div class="recent-row"><span class="recent-lbl">ใช้บ่อย</span><div class="recent-chips">${chips}</div></div>`;
}

function filterCodes(q: string): ExpenseCode[] {
  const s = q.trim().toLowerCase();
  const list = allCodes();
  if (!s) return list;
  return list.filter(
    (c) => c.code.toLowerCase().includes(s) || c.name.toLowerCase().includes(s),
  );
}

function codeListHtml(q: string, selectedCode: string | null): string {
  const codes = filterCodes(q);
  if (codes.length === 0) return '<p class="muted" style="padding:8px">ไม่พบโค้ด — กด “เพิ่มโค้ดของฉัน”</p>';
  const byFam = new Map<string, ExpenseCode[]>();
  for (const c of codes) {
    const arr = byFam.get(c.family) ?? [];
    arr.push(c);
    byFam.set(c.family, arr);
  }
  const fams = FAMILY_ORDER.filter((f) => byFam.has(f));
  // custom families (นอกลำดับ) ต่อท้าย
  for (const f of byFam.keys()) if (!fams.includes(f as ExpenseFamily)) fams.push(f as ExpenseFamily);
  return fams
    .map((fam) => {
      const rows = byFam
        .get(fam)!
        .map((c) => {
          const sel = c.code === selectedCode ? ' sel' : '';
          const special = c.needsReview ? ' special' : '';
          return `<button type="button" class="codeitem${sel}${special}" data-code="${esc(c.code)}" role="radio" aria-checked="${c.code === selectedCode}">
            <span class="code-chip">${esc(c.code)}</span>
            <span class="code-mid"><span class="code-name">${esc(c.name)}</span></span>
            <span class="code-mark">${c.needsReview ? '⚠️' : c.code === selectedCode ? '✓' : '›'}</span>
          </button>`;
        })
        .join('');
      const m = famMeta(fam);
      return `<div class="code-fam-group"><div class="code-fam-h"><span class="fam-ic">${m.icon}</span><span class="fam-label">${esc(m.label)}</span></div>${rows}</div>`;
    })
    .join('');
}

function autofillHtml(c: ExpenseCode): string {
  const catLabel = CATEGORY_CHOICES.find(([v]) => v === c.pnlCategory)?.[1] ?? c.pnlCategory ?? '—';
  const wht = c.whtRate !== undefined ? `หัก ${c.whtRate}%` : c.whtNote ? c.whtNote : 'ไม่หัก';
  const vatLabel =
    c.vat === 'claimable'
      ? 'ขอคืนได้'
      : c.vat === 'non_claimable'
        ? 'ต้องห้าม'
        : c.vat === 'exempt'
          ? 'ยกเว้น'
          : c.vat === 'deemed'
            ? 'Deemed Sale'
            : c.vat === 'pp36'
              ? 'ภ.พ.36'
              : '—';
  const warnBlock = c.warn
    ? `<div class="code-warn">⚠️ ${esc(c.warn)}</div>`
    : '';
  const cat = c.classification === 'expense' ? `หมวด: <b>${esc(catLabel)}</b>` : `<b>ไม่ใช่ค่าใช้จ่ายปกติ</b> (${esc(c.classification)})`;
  return `<div class="autofill">
    <span class="l">ระบบเติมให้จาก ${esc(c.code)}</span>
    <div class="af-row"><span class="af">${cat}</span><span class="af">WHT: <b>${esc(wht)}</b></span><span class="af">VAT: <b>${esc(vatLabel)}</b></span></div>
    ${c.note ? `<div class="code-note-line">${esc(c.note)}</div>` : ''}
    ${warnBlock}
  </div>`;
}

/** CTA พาไปหน้าที่ถูก เมื่อเลือกโค้ดที่ไม่ใช่ค่าใช้จ่ายปกติ (asset/liability/tax) */
function redirectCtaHtml(c: ExpenseCode): string {
  const r = expenseCodeRedirect(c);
  if (!r) return '';
  return `<div class="redirect-cta" role="note">
    <div class="rc-head">⚠️ <b>${esc(c.name)}</b> เป็น<b>${esc(r.kindLabel)}</b> ไม่ใช่ค่าใช้จ่ายปกติ</div>
    ${c.warn ? `<div class="rc-warn">${esc(c.warn)}</div>` : ''}
    <div class="rc-why">บันทึกที่นี่เป็นค่าใช้จ่ายจะทำให้กำไรผิด — ให้ไปบันทึกที่หน้า “${esc(r.pageLabel)}” แทน</div>
    <a class="btn rc-go" href="#${esc(r.route)}">ไปหน้า${esc(r.pageLabel)} →</a>
  </div>`;
}

/** แผงเตือนเพดานบริจาค (advisory) — โชว์เมื่อเลือกโค้ด 40a/40b */
function donationPanelHtml(c: ExpenseCode): string {
  const kind = donationKindOfCode(c.code);
  if (!kind) return '';
  const rule = DONATION_RULES[kind];
  return `<div class="donation-advice" role="note">
    <div class="da-rule">🙏 ${esc(rule.ruleText)}</div>
    <details class="da-est">
      <summary>ตรวจเพดานบริจาค (ประมาณ)</summary>
      <label class="da-net">กำไรสุทธิทั้งปีโดยประมาณ (บาท)
        <input type="text" inputmode="decimal" id="don-net" placeholder="เช่น 1,000,000" autocomplete="off" />
      </label>
      <div id="don-result" class="da-result muted">กรอกกำไรสุทธิ + จำนวนเงินบริจาค เพื่อดูเพดาน</div>
      <p class="da-foot">ประมาณการเพื่อเตือนเท่านั้น — ภาษีจริงยืนยันกับฝ่ายบัญชี</p>
    </details>
  </div>`;
}

/** สรุปภาษีสั้น ๆ ต่อโค้ด (สำหรับ guideline) */
function codeTaxLabel(c: ExpenseCode): string {
  const parts: string[] = [];
  if (c.whtRate !== undefined) parts.push(`หัก ${c.whtRate}%`);
  else if (c.whtNote) parts.push(c.whtNote);
  const vat: Record<string, string> = {
    claimable: 'VAT ขอคืนได้',
    non_claimable: 'VAT ต้องห้าม',
    exempt: 'VAT ยกเว้น',
    deemed: 'Deemed Sale',
    pp36: 'ภ.พ.36',
  };
  if (c.vat && vat[c.vat]) parts.push(vat[c.vat]!);
  return parts.join(' · ') || 'ไม่มีภาษีพิเศษ';
}

/** เนื้อกรอบขวา — จัดกลุ่มตาม family, พับได้ (details), กรองด้วย q */
export function guideBodyHtml(q = ''): string {
  const codes = filterCodes(q);
  if (codes.length === 0)
    return '<p class="muted" style="padding:10px">ไม่พบโค้ดที่ตรงกับคำค้น</p>';
  const searching = q.trim() !== '';
  const byFam = new Map<string, ExpenseCode[]>();
  for (const c of codes) {
    const arr = byFam.get(c.family) ?? [];
    arr.push(c);
    byFam.set(c.family, arr);
  }
  const fams = FAMILY_ORDER.filter((f) => byFam.has(f));
  for (const f of byFam.keys()) if (!fams.includes(f as ExpenseFamily)) fams.push(f as ExpenseFamily);
  return fams
    .map((fam) => {
      const rows = byFam
        .get(fam)!
        .map(
          (c) => `<button type="button" class="guide-row${c.needsReview ? ' special' : ''}" data-code="${esc(c.code)}" title="เลือกโค้ดนี้">
            <div class="guide-top"><span class="code-chip">${esc(c.code)}</span><span class="guide-name">${esc(c.name)}</span>${c.needsReview ? '<span class="guide-warn">⚠️</span>' : ''}</div>
            <div class="guide-tax">${esc(codeTaxLabel(c))}</div>
          </button>`,
        )
        .join('');
      const m = famMeta(fam);
      // เปิดกลุ่มไว้เมื่อค้นหา (เห็นผลทันที); ปกติพับได้แต่เปิดค้างไว้ให้เห็นทั้งหมด
      return `<details class="guide-fam${fam === 'พิเศษ (ตรวจสอบก่อน)' ? ' guide-fam-special' : ''}" open>
        <summary class="guide-fam-h"><span class="fam-ic">${m.icon}</span><span class="fam-titles"><span class="fam-label">${esc(m.label)}</span>${m.hint && !searching ? `<span class="fam-hint">${esc(m.hint)}</span>` : ''}</span><span class="fam-count">${byFam.get(fam)!.length}</span></summary>
        <div class="guide-fam-rows">${rows}</div>
      </details>`;
    })
    .join('');
}

/** กรอบขวา — โค้ดทั้งหมดเป็น guideline (อ้างอิง + ค้นหา + พับ + คลิกเลือกได้) */
function guidelineHtml(): string {
  const list = allCodes();
  return `<aside class="code-guide" aria-label="คู่มือโค้ดค่าใช้จ่าย">
    <div class="guide-head"><strong>คู่มือโค้ดค่าใช้จ่าย</strong><span class="muted">${list.length} โค้ด · คลิกเพื่อเลือก</span></div>
    <div class="search guide-search"><span>🔎</span><input type="text" id="guide-search" placeholder="ค้นหาในคู่มือ…" autocomplete="off" aria-label="ค้นหาในคู่มือโค้ด" /></div>
    <div class="guide-list" id="guide-list">${guideBodyHtml('')}</div>
    <p class="guide-foot">ค่าภาษีเป็นค่าตั้งต้นอ้างอิงประมวลรัษฎากร — ยืนยันกับฝ่ายบัญชีก่อนใช้จริง</p>
  </aside>`;
}

// ─────────────────────────────────────────────────────────────
export const simpleExpensePage: Page = {
  title: 'บันทึกรายรับ-รายจ่าย',
  render() {
    const catOptions = CATEGORY_CHOICES.map(
      ([v, l]) => `<option value="${esc(v)}">${esc(l)}</option>`,
    ).join('');
    return `
      <div class="topbar"><h1>บันทึกรายรับ-รายจ่าย</h1></div>
      <div class="entry-layout">
      <div class="card simple-entry">
        <form id="simple-form">
          <div class="dir-toggle" role="radiogroup" aria-label="ประเภทรายการ">
            <button type="button" class="dir-btn on" data-dir="expense" aria-checked="true" role="radio">− จ่ายเงิน</button>
            <button type="button" class="dir-btn" data-dir="income" aria-checked="false" role="radio">+ รับเงิน</button>
          </div>

          <div id="code-wrap">
            <label class="fld-lbl" for="code-search">จ่ายค่าอะไร? เลือกโค้ด</label>
            <div id="recent-slot">${recentChipsHtml()}</div>
            <div class="search"><span>🔎</span><input type="text" id="code-search" placeholder="พิมพ์หา… แพ็ค / แอด / เช่า / ดอกเบี้ย" autocomplete="off" /></div>
            <div class="codelist" id="codelist" role="radiogroup" aria-label="โค้ดค่าใช้จ่าย">${codeListHtml('', null)}</div>
            <button type="button" class="addbtn" id="toggle-add">＋ เพิ่มโค้ดของฉัน</button>
            <div class="custom-add" id="custom-add" hidden>
              <div class="ca-grid">
                <label>ชื่อโค้ด<input type="text" id="ca-name" placeholder="เช่น ค่าสมาชิกสมาคม" /></label>
                <label>เข้าหมวด<select id="ca-cat">${catOptions}</select></label>
                <label>หัก ณ ที่จ่าย %<input type="text" inputmode="decimal" id="ca-wht" placeholder="0" /></label>
              </div>
              <button type="button" class="ca-save" id="ca-save">บันทึกโค้ดใหม่ + เลือกใช้</button>
            </div>
            <div id="autofill-slot"></div>
            <div id="redirect-slot"></div>
            <div id="donation-slot"></div>
          </div>

          <label class="big-amount">จำนวนเงิน (บาท)
            <input type="text" inputmode="decimal" id="s-amount" placeholder="0.00" autocomplete="off" />
          </label>

          <label class="fld-lbl">วันที่<input type="date" id="s-date" value="${todayKey()}" /></label>

          <details class="more-details">
            <summary>＋ เพิ่มรายละเอียด (ไม่บังคับ)</summary>
            <div class="more-grid">
              <label class="cp-lbl">ใครรับเงิน / คู่ค้า<input type="text" id="s-counterparty" placeholder="ชื่อร้าน/ผู้รับเงิน" /></label>
              <label>เลขที่ใบเสร็จ/บิล<input type="text" id="s-docno" placeholder="เช่น INV-001" /></label>
              <label>VAT ในบิล (ถ้ามี)<input type="text" inputmode="decimal" id="s-vat" placeholder="0.00" /></label>
              <label>หัก ณ ที่จ่าย (บาท)<input type="text" inputmode="decimal" id="s-wht" placeholder="0.00" /></label>
              <label>จ่ายด้วยอะไร
                <select id="s-pay"><option value="">—</option><option value="cash">เงินสด</option><option value="bank_transfer">โอนธนาคาร</option><option value="promptpay">พร้อมเพย์</option><option value="credit_card">บัตรเครดิต</option></select>
              </label>
              <label class="chk-lbl"><input type="checkbox" id="s-unpaid" /> ยังไม่ได้จ่าย (ค้างจ่าย)</label>
              <label>หมายเหตุ<input type="text" id="s-note" placeholder="รายละเอียดเพิ่มเติม" /></label>
            </div>
          </details>

          <div class="form-actions">
            <button type="submit" class="btn-save" id="s-save">บันทึก</button>
            <span id="s-result" class="muted"></span>
          </div>
        </form>
      </div>
      ${guidelineHtml()}
      </div>`;
  },
  mount(root) {
    const form = root.querySelector<HTMLFormElement>('#simple-form')!;
    const codeWrap = root.querySelector<HTMLElement>('#code-wrap')!;
    const codelist = root.querySelector<HTMLElement>('#codelist')!;
    const search = root.querySelector<HTMLInputElement>('#code-search')!;
    const autofillSlot = root.querySelector<HTMLElement>('#autofill-slot')!;
    const redirectSlot = root.querySelector<HTMLElement>('#redirect-slot')!;
    const donationSlot = root.querySelector<HTMLElement>('#donation-slot')!;
    const amount = root.querySelector<HTMLInputElement>('#s-amount')!;
    const result = root.querySelector<HTMLElement>('#s-result')!;
    const save = root.querySelector<HTMLButtonElement>('#s-save')!;

    let direction: Direction = 'expense';
    let selected: ExpenseCode | null = null;

    const codeByCode = (code: string): ExpenseCode | undefined =>
      allCodes().find((c) => c.code === code);

    const rerenderList = (): void => {
      codelist.innerHTML = codeListHtml(search.value, selected?.code ?? null);
      wireCodeButtons();
    };
    // ตรวจเพดานบริจาค (advisory) — คำนวณสดจากกำไรสุทธิที่กรอก + จำนวนเงินบริจาค
    const recomputeDonation = (): void => {
      const kind = selected ? donationKindOfCode(selected.code) : null;
      const resEl = donationSlot.querySelector<HTMLElement>('#don-result');
      if (!kind || !resEl) return;
      const netEl = donationSlot.querySelector<HTMLInputElement>('#don-net');
      const net = parseMoney(netEl?.value);
      const amt = parseMoney(amount.value);
      if (!net || !amt || Number(amt) <= 0) {
        resEl.className = 'da-result muted';
        resEl.textContent = 'กรอกกำไรสุทธิ + จำนวนเงินบริจาค เพื่อดูเพดาน';
        return;
      }
      const d = computeDonationDeduction({ kind, amount: amt, netProfitBeforeDonations: net });
      const cap = formatTHB(d.capAmount);
      const allowed = formatTHB(d.allowedDeduction);
      if (d.overCap) {
        resEl.className = 'da-result over';
        resEl.innerHTML = `⚠️ เกินเพดาน — เพดานหักได้ <b>${cap}</b> (หักจริง <b>${allowed}</b>) · ส่วนเกิน <b>${formatTHB(d.excessDeduction)}</b> หักภาษีไม่ได้`;
      } else {
        resEl.className = 'da-result ok';
        resEl.innerHTML = `✓ อยู่ในเพดาน — หักได้ <b>${allowed}</b> (เพดาน ${cap})`;
      }
    };

    const selectCode = (code: string): void => {
      selected = codeByCode(code) ?? null;
      autofillSlot.innerHTML = selected ? autofillHtml(selected) : '';
      // โค้ดที่ไม่ใช่ค่าใช้จ่ายปกติ (asset/liability/tax) → CTA พาไปหน้าที่ถูก + ปิดปุ่มบันทึก
      const redirect = selected ? expenseCodeRedirect(selected) : null;
      redirectSlot.innerHTML = selected ? redirectCtaHtml(selected) : '';
      const blocked = redirect !== null;
      save.disabled = blocked;
      save.title = blocked ? 'รายการนี้ต้องบันทึกที่หน้าอื่น' : '';
      // เพดานบริจาค (40a/40b)
      donationSlot.innerHTML = selected ? donationPanelHtml(selected) : '';
      donationSlot
        .querySelector<HTMLInputElement>('#don-net')
        ?.addEventListener('input', recomputeDonation);
      rerenderList();
    };
    function wireCodeButtons(): void {
      codelist.querySelectorAll<HTMLButtonElement>('.codeitem[data-code]').forEach((b) =>
        b.addEventListener('click', () => selectCode(b.dataset['code']!)),
      );
    }
    wireCodeButtons();
    search.addEventListener('input', rerenderList);
    amount.addEventListener('input', recomputeDonation);

    // กรอบขวา (guideline) — ค้นหา + พับ + คลิกโค้ดเพื่อเลือกใช้ในฟอร์ม
    const guideList = root.querySelector<HTMLElement>('#guide-list')!;
    const guideSearch = root.querySelector<HTMLInputElement>('#guide-search');
    function wireGuideButtons(): void {
      guideList.querySelectorAll<HTMLButtonElement>('.guide-row[data-code]').forEach((b) =>
        b.addEventListener('click', () => {
          if (codeWrap.hidden) return; // โหมดรับเงินไม่ใช้โค้ด
          selectCode(b.dataset['code']!);
          codeWrap.scrollIntoView({ block: 'nearest' });
        }),
      );
    }
    const rebuildGuide = (): void => {
      guideList.innerHTML = guideBodyHtml(guideSearch?.value ?? '');
      wireGuideButtons();
    };
    wireGuideButtons();
    guideSearch?.addEventListener('input', rebuildGuide);

    // แถบ "ใช้บ่อย" — กดชิปเลือกโค้ดที่เพิ่งใช้ได้ทันที (ข้ามการค้นหา)
    const recentSlot = root.querySelector<HTMLElement>('#recent-slot')!;
    const wireRecentChips = (): void => {
      recentSlot.querySelectorAll<HTMLButtonElement>('.recent-chip[data-code]').forEach((b) =>
        b.addEventListener('click', () => selectCode(b.dataset['code']!)),
      );
    };
    wireRecentChips();

    // โหลดโค้ดที่ผู้ใช้เพิ่มเอง (backend ต่อกิจการ; demo → localStorage) แล้วเติมลิสต์
    void loadCustomCodes().then(() => {
      rerenderList();
      rebuildGuide();
      recentSlot.innerHTML = recentChipsHtml();
      wireRecentChips();
      const head = root.querySelector<HTMLElement>('.guide-head .muted');
      if (head) head.textContent = `${allCodes().length} โค้ด · คลิกเพื่อเลือก`;
    });

    // จ่าย/รับ — รับเงินซ่อนตัวเลือกโค้ด (และปลดล็อกปุ่มบันทึก)
    root.querySelectorAll<HTMLButtonElement>('.dir-btn[data-dir]').forEach((b) =>
      b.addEventListener('click', () => {
        direction = b.dataset['dir'] as Direction;
        root.querySelectorAll<HTMLButtonElement>('.dir-btn').forEach((x) => {
          const on = x === b;
          x.classList.toggle('on', on);
          x.setAttribute('aria-checked', String(on));
        });
        codeWrap.hidden = direction === 'income';
        // รับเงิน = บันทึกได้เสมอ; รายจ่าย = ล็อกถ้าโค้ดที่เลือกเป็น asset/liability/tax
        const blocked = direction === 'expense' && selected !== null && expenseCodeRedirect(selected) !== null;
        save.disabled = blocked;
      }),
    );

    // เพิ่มโค้ดเอง
    const customBox = root.querySelector<HTMLElement>('#custom-add')!;
    root.querySelector<HTMLButtonElement>('#toggle-add')!.addEventListener('click', () => {
      customBox.hidden = !customBox.hidden;
    });
    const caSave = root.querySelector<HTMLButtonElement>('#ca-save')!;
    caSave.addEventListener('click', () => {
      const name = root.querySelector<HTMLInputElement>('#ca-name')!.value.trim();
      if (!name) {
        root.querySelector<HTMLInputElement>('#ca-name')!.focus();
        return;
      }
      const cat = root.querySelector<HTMLSelectElement>('#ca-cat')!.value;
      const whtRaw = root.querySelector<HTMLInputElement>('#ca-wht')!.value.trim();
      const whtRate = whtRaw ? Number(whtRaw) : undefined;
      const code = `ของฉัน-${name.slice(0, 16)}`;
      const custom: ExpenseCode = {
        code,
        name,
        family: 'ดำเนินงานอื่น',
        classification: 'expense',
        pnlCategory: cat,
        ...(whtRate !== undefined && Number.isFinite(whtRate) ? { whtRate } : {}),
        note: 'โค้ดที่เพิ่มเอง',
      };
      caSave.disabled = true;
      persistCustomCode(custom)
        .then(() => {
          customBox.hidden = true;
          search.value = '';
          selectCode(code);
          rebuildGuide();
        })
        .catch((err: unknown) => {
          result.className = 'error';
          result.textContent = err instanceof Error ? err.message : 'บันทึกโค้ดไม่สำเร็จ';
        })
        .finally(() => {
          caSave.disabled = false;
        });
    });

    const val = (id: string): string =>
      (root.querySelector<HTMLInputElement>(id)?.value ?? '').trim();

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      let body: Record<string, unknown>;
      try {
        body = buildSimpleEntryBody({
          direction,
          ...(selected ? { code: selected } : {}),
          amount: amount.value,
          date: val('#s-date'),
          counterparty: val('#s-counterparty'),
          docNo: val('#s-docno'),
          vat: val('#s-vat'),
          wht: val('#s-wht'),
          paymentMethod: val('#s-pay'),
          unpaid: root.querySelector<HTMLInputElement>('#s-unpaid')?.checked ?? false,
          note: val('#s-note'),
        });
      } catch (err) {
        result.className = 'error';
        result.textContent = err instanceof Error ? err.message : 'ข้อมูลไม่ถูกต้อง';
        return;
      }

      if (!getTenant()) {
        const dirLabel = direction === 'income' ? 'รับเงิน' : `จ่าย ${selected?.name ?? ''}`;
        result.className = 'muted';
        result.innerHTML = `ตัวอย่าง (โหมด demo): ${esc(dirLabel)} ${formatTHB(body['totalAmount'] as string)} — เข้าสู่ระบบเพื่อบันทึกจริง`;
        return;
      }

      save.disabled = true;
      result.className = 'muted';
      result.textContent = 'กำลังบันทึก…';
      const usedCode = selected?.code;
      createEntry(body)
        .then(() => {
          result.className = 'ok-text';
          result.innerHTML = `✓ บันทึกแล้ว (<a class="btn-link" href="#entries">ดูในรายการ</a>)`;
          // จำโค้ดที่เพิ่งใช้ → ครั้งหน้ากดชิป "ใช้บ่อย" ได้เลย
          if (direction === 'expense' && usedCode) {
            recordRecentCode(usedCode);
            recentSlot.innerHTML = recentChipsHtml();
            wireRecentChips();
          }
          form.reset();
          const dateEl = root.querySelector<HTMLInputElement>('#s-date');
          if (dateEl) dateEl.value = todayKey();
          amount.focus();
        })
        .catch((err: unknown) => {
          result.className = 'error';
          result.textContent = err instanceof Error ? err.message : 'บันทึกไม่สำเร็จ';
        })
        .finally(() => {
          save.disabled = false;
        });
    });

    amount.focus();
  },
};
